import type { TypographyProps } from '@iheartjane/reefer';
import type { Nullish } from '@iheartjane/types';

export interface TextDetailsArgs {
  /**
   * Brand and brand subtype
   * @example "WYLD Gummies"
   */
  brandInfo?: Nullish<string>;
  /**
   * Dosage info (often in edibles) - useful as an alternative or to supplement lab results.
   * @example "100mg THC/100mg CBD/100mg CBN 10pk"
   * @example "100mg 10pk"
   */
  dosageInfo?: Nullish<string>;
  /**
   * True when the product has multiple weights and a discount is in effect.
   * This is used to shorten the product name, to leave additional room below for
   * discounted pricing.
   */
  hasMultipleWeightDiscount?: boolean;
  isSponsored?: boolean;
  /**
   * Product name
   * @example "Elderberry 2:1 CBN + Indica Enhanced Gummies"
   */
  name?: Nullish<string>;
  /**
   * Lab results
   * @example "THC 24.67% CBD 0.16%"
   */
  potencyInfo?: Nullish<string>;
}

type ProductCardTextLineId =
  'brand' | 'dosage' | 'name' | 'potency' | 'sponsored';

interface Line extends Omit<TypographyProps, 'children'> {
  lineId: ProductCardTextLineId;
  text: string;
}

interface IntermediateLine extends Omit<TypographyProps, 'children'> {
  lineId: ProductCardTextLineId;
  text?: Nullish<string>;
}

/**
 * Manages presentational styles of up to 5 full-width lines of
 * text describing a product.
 * Does *not* include the first line (category label and review count)
 * or pricing info, which can occupy 1-2 lines of its own.
 *
 * Discards blank values.
 *
 * ```
 * Sponsored (optional)
 * Product name (1 or 2 lines)
 * Brand and brand subtype
 * Potency info
 * Dosage info
 * ```
 * @example
 * ```
 * Sponsored
 * Elderberry 2:1 CBN + Indica Enhanced
 *  Gummies | 100mg THC:50mg CBN
 * WYLD Gummies
 * THC 0.23% CBD 0.20%
 * 100mg THC/50mg CBN 10pk
 * ```
 */
export const productCardTextDetails = ({
  brandInfo,
  dosageInfo,
  hasMultipleWeightDiscount = false,
  isSponsored,
  name,
  potencyInfo,
}: TextDetailsArgs): Line[] => {
  const lines: IntermediateLine[] = [
    {
      color: 'grays-mid',
      maxLines: 1,
      lineId: 'sponsored',
      text: isSponsored ? 'Sponsored' : null,
      variant: 'small-body-regular',
    },
    {
      color: 'text-main',
      'data-testid': 'product-name',
      maxLines:
        /** Shorten the product name if we need to make room for multiple weights (which spans 2 lines) */
        hasMultipleWeightDiscount ? 1 : 2,
      lineId: 'name',
      text: name,
      variant: 'body-semibold',
    },
    {
      color: 'text-main',
      maxLines: 1,
      lineId: 'brand',
      text: brandInfo,
      variant: 'small-body-regular',
    },
    {
      color: 'text-main',
      maxLines: 1,
      lineId: 'potency',
      text: potencyInfo,
      variant: 'small-body-regular',
    },
    {
      color: 'text-main',
      maxLines: 1,
      lineId: 'dosage',
      text: dosageInfo,
      variant: 'small-body-regular',
    },
  ];

  return lines
    .filter((line): line is Line => !!line.text)
    .slice(0, hasMultipleWeightDiscount ? 4 : 5);
};
